<section class="px-6 py-20 sm:px-10">
  <div class="mx-auto max-w-4xl">
    <p class="mb-6">
      <a
        [routerLink]="['/competitions', competitionId]"
        class="text-sm text-dusk hover:text-parchment hover:underline"
        >&larr; Back to competition</a
      >
    </p>

    @if (loading()) {
      <p class="text-dusk">Loading…</p>
    } @else if (errorMessage()) {
      <p class="rounded border border-wax/40 bg-wax/10 px-4 py-3 text-sm text-parchment">
        {{ errorMessage() }}
      </p>
    } @else {
      <div class="mb-8">
        <span class="mb-4 block text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-gold"
          >Your picks</span
        >
        <h1 class="font-display text-3xl font-semibold text-parchment">
          {{ competition()?.name }}
        </h1>
      </div>

      @if (pastCutoff()) {
        <p class="mb-6 rounded border border-wax-bright/40 bg-wax/10 px-4 py-3 text-sm text-parchment">
          The submission deadline for this competition has passed — picks are locked.
        </p>
        @if (myTotalPoints(); as total) {
          <div class="mb-6 rounded-md border border-hairline bg-panel p-6">
            <h2 class="text-sm font-semibold uppercase tracking-wide text-dusk">Your score</h2>
            <p class="mt-2 text-3xl font-semibold text-gold">{{ total }} pts</p>
          </div>
        }
      } @else {
        <div class="relative mb-6">
          <form
            [formGroup]="searchForm"
            (ngSubmit)="search()"
            class="flex gap-3"
          >
            <input
              formControlName="q"
              type="text"
              placeholder="Search for a celebrity…"
              class="flex-1 rounded border border-hairline bg-ink px-4 py-2.5 text-parchment placeholder:text-dusk/60 focus:border-gold focus:outline-none"
            />
            <button
              type="submit"
              [disabled]="searching()"
              class="inline-flex shrink-0 items-center justify-center rounded-[3px] bg-gradient-to-b from-gold to-gold-dim px-5 py-2.5 text-sm font-bold text-ink transition hover:-translate-y-px disabled:cursor-not-allowed disabled:opacity-60"
            >
              {{ searching() ? 'Searching…' : 'Search' }}
            </button>
          </form>

          <!-- Floats over the picks grid instead of pushing it down; closes
               entirely once a pick is added (see addPick()). -->
          <div
            class="absolute left-0 right-0 top-[calc(100%+0.5rem)] z-10 flex flex-col gap-1 rounded-md border border-hairline bg-panel p-2 shadow-xl transition-all duration-150 ease-out"
            [class.opacity-100]="searchResults().length > 0"
            [class.opacity-0]="searchResults().length === 0"
            [class.pointer-events-none]="searchResults().length === 0"
            [class.-translate-y-1]="searchResults().length === 0"
          >
            <ng-template #resultRow let-result>
              <div class="flex items-start justify-between gap-4 rounded p-3 transition hover:bg-parchment/[0.04]">
                <div class="flex items-start gap-3">
                  @if (result.imageUrl) {
                    <img
                      [src]="result.imageUrl"
                      alt=""
                      class="h-12 w-12 shrink-0 rounded-full object-cover"
                    />
                  }
                  <div>
                    <p class="font-medium text-parchment">
                      {{ result.name }}{{ result.aliases?.length ? ' "' + result.aliases.join('", "') + '"' : '' }}{{ result.knownFor ? ' (' + result.knownFor + ')' : '' }}
                    </p>
                    <p class="mt-1 text-xs uppercase tracking-wide text-dusk">
                      {{ result.source }}
                      @if (result.status === 'DEAD') {
                        · Deceased
                      }
                      @if (result.source === 'wikipedia' && result.dateOfBirth === null) {
                        · no confirmed birth date
                      }
                    </p>
                  </div>
                </div>
                @if (result.status !== 'DEAD') {
                  <div class="flex shrink-0 gap-2">
                    <button
                      type="button"
                      (click)="addPick(result, false)"
                      [disabled]="regularCapReached() || addingKey() === resultKey(result, false)"
                      class="rounded-[3px] border border-hairline px-3 py-1.5 text-xs font-semibold text-parchment transition hover:border-parchment disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      + Regular
                    </button>
                    <button
                      type="button"
                      (click)="addPick(result, true)"
                      [disabled]="alternateCapReached() || addingKey() === resultKey(result, true)"
                      class="rounded-[3px] border border-hairline px-3 py-1.5 text-xs font-semibold text-parchment transition hover:border-parchment disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      + Alternate
                    </button>
                  </div>
                }
              </div>
            </ng-template>

            @for (result of visibleResults(); track resultKey(result, false)) {
              <ng-container
                [ngTemplateOutlet]="resultRow"
                [ngTemplateOutletContext]="{ $implicit: result }"
              />
            }

            <!-- Non-person Wikipedia noise (a celebrity's movies/discography,
                 etc.) — filtered out by default since they have no confirmed
                 birth date, but still reachable here in case the filter is
                 wrong about a real person with only a low-precision one. -->
            @if (hiddenResults().length > 0) {
              <button
                type="button"
                (click)="showHidden.set(!showHidden())"
                class="rounded px-3 py-1.5 text-left text-xs text-dusk transition hover:bg-parchment/[0.04] hover:text-parchment hover:underline"
              >
                {{ showHidden() ? 'Hide' : 'Show ' + hiddenResults().length + ' Hidden' }}
              </button>
              @if (showHidden()) {
                @for (result of hiddenResults(); track resultKey(result, false)) {
                  <ng-container
                    [ngTemplateOutlet]="resultRow"
                    [ngTemplateOutletContext]="{ $implicit: result }"
                  />
                }
              }
            }

            @if (visibleResults().length > 0 && !wikipediaAlsoSearched()) {
              <button
                type="button"
                (click)="search(true)"
                [disabled]="searching()"
                class="rounded px-3 py-1.5 text-left text-xs text-dusk transition hover:bg-parchment/[0.04] hover:text-parchment hover:underline disabled:cursor-not-allowed disabled:opacity-50"
              >
                Not the right person? Search Wikipedia
              </button>
            }
          </div>
        </div>

        @if (noResultsFound()) {
          <p class="mb-6 rounded border border-hairline bg-panel px-4 py-3 text-sm text-dusk">
            No results found — nothing in the catalog or on Wikipedia matched that search.
          </p>
        }

        @if (searchError()) {
          <p class="mb-6 rounded border border-wax/40 bg-wax/10 px-4 py-3 text-sm text-parchment">
            {{ searchError() }}
          </p>
        }
      }

      <div class="rounded-md border border-hairline bg-panel p-6">
        <h2 class="text-sm font-semibold uppercase tracking-wide text-dusk">
          Your picks
          <span class="ml-1 normal-case tracking-normal text-dusk/70">
            (Regular {{ regularPicks().length }}/{{ competition()?.maxEntries }} ·
            Alternate {{ alternatePicks().length }}/{{ competition()?.maxAlternates }})
          </span>
        </h2>

        @if (deathCheckNotice()) {
          <p class="mt-3 rounded border border-hairline bg-ink/40 px-3 py-2 text-xs text-dusk">
            {{ deathCheckNotice() }}
          </p>
        }

        @if (sortedPicks().length === 0) {
          <p class="mt-3 text-sm text-dusk">No picks yet.</p>
        } @else {
          <ul class="mt-3 flex flex-col gap-2">
            @for (pick of sortedPicks(); track pick.id) {
              <li
                class="rounded border border-hairline px-4 py-2 text-sm"
                [class.pick-flash]="newlyAddedPickId() === pick.id || justDiedPickId() === pick.id"
              >
                <div class="flex items-center justify-between gap-3">
                  <span class="flex flex-wrap items-center gap-2 text-parchment">
                    @if (pick.person.imageUrl) {
                      <img
                        [src]="pick.person.imageUrl"
                        alt=""
                        class="h-7 w-7 shrink-0 rounded-full object-cover"
                      />
                    }
                    {{ pick.person.name }}
                    @if (ageFor(pick.person) !== null) {
                      <span class="text-xs text-dusk">Age {{ ageFor(pick.person) }}</span>
                    }
                    @if (pick.isAlternate) {
                      <span
                        class="rounded-sm bg-gold/15 px-1.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-gold"
                        >Alternate</span
                      >
                    }
                    @if (pick.promotedAt) {
                      <span class="text-xs text-gold">(promoted)</span>
                    }
                  </span>
                  <span class="flex shrink-0 items-center gap-3">
                    @if (pick.person.status === 'ALIVE' && pick.person.wikipediaUrl) {
                      <button
                        type="button"
                        (click)="requestDeathCheck(pick)"
                        [disabled]="deathChecking() === pick.id"
                        class="text-xs text-dusk hover:text-parchment hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {{ deathChecking() === pick.id ? 'Checking…' : 'Death Check' }}
                      </button>
                    }
                    @if (pastCutoff()) {
                      @if (pick.person.status === 'DEAD') {
                        <span class="text-xs font-semibold text-wax-bright">Deceased</span>
                      }
                    } @else {
                      <button
                        type="button"
                        (click)="removePick(pick)"
                        class="text-xs text-wax-bright hover:underline"
                      >
                        Remove
                      </button>
                    }
                  </span>
                </div>
                @if (pastCutoff() && awardsForPick(pick).length > 0) {
                  <ul class="mt-1.5 flex flex-col gap-1">
                    @for (award of awardsForPick(pick); track award.achievementId) {
                      <li class="flex items-center justify-between text-xs text-gold">
                        <span>{{ award.name }}</span>
                        <span>{{ awardValue(award) }}</span>
                      </li>
                    }
                  </ul>
                }
              </li>
            }
          </ul>
        }
      </div>
    }
  </div>
</section>
